import * as R from 'ramda';
import uniqid from 'uniqid';
import { all, select, fork, cancel, race, call, put, take, takeEvery, takeLatest, cancelled } from 'redux-saga/effects';
import { go, push, replace } from 'redux-little-router';
import queryWorkflow from './querySaga';
import categoryWorkflow from './categorySaga';
import browseWorkflow from './browseSaga';
import dbTabsSagas from '../db/dbTabsSagas';

const routeWatcher = function* routeWatcher(connectionId) {
  while (true) {
    const { open, create } = yield race({
      open: take(action => (
        action.type === 'ROUTER_LOCATION_CHANGED' &&
        R.path(['payload', 'pathname'], action) === '/tab' &&
        R.path(['payload', 'query', 'type'], action) &&
        R.path(['payload', 'query', 'recordId'], action)
      )),
      create: take(action => (
        action.type === 'ROUTER_LOCATION_CHANGED' &&
        R.path(['payload', 'pathname'], action) === '/tab' &&
        R.path(['payload', 'query', 'type'], action) &&
        !R.path(['payload', 'query', 'recordId'], action)
      )),
    });

    const { type, recordId } = R.path(['payload', 'query'], open || create);
    let existingTab;

    if (open) {
      existingTab = yield select(store => (
        R.pathOr([], ['tab', `/connection/${connectionId}`, 'ids'], store)
          .map(id => store.tab.byId[id])
          .find((tab) => {
            if (tab.category) {
              return tab.category.sourceId === recordId;
            }
            if (tab.query) {
              return tab.query.id === parseInt(recordId, 10);
            }
            return false;
          })
      ));
    }

    if (!existingTab) {
      const tab = yield call(dbTabsSagas.openSaga, connectionId, type, recordId);

      yield put(replace(`/tab/${tab.id}`));
    } else {
      yield put(replace(`/tab/${existingTab.id}`));
    }
  }
};

const releaseWatcher = function* releaseWatcher(connectionId, activeTabInstances) {
  while (true) {
    const releaseAction = yield take(action => action.type === 'RELEASE_KEY_WINDOW');

    if (activeTabInstances.get(releaseAction.payload.keyWindow)) {
      yield put(replace('/tab/browse'));
      yield cancel(activeTabInstances.get(releaseAction.payload.keyWindow));
      activeTabInstances.delete(releaseAction.payload.keyWindow);
      yield call(dbTabsSagas.closeSaga, connectionId, releaseAction.payload.keyWindow.split('/').pop());
    }
  }
};

const tabOrchestrator = function* tabOrchestrator(connectionId) {
  const activeTabInstances = new Map();
  yield fork(routeWatcher, connectionId);
  yield fork(releaseWatcher, connectionId, activeTabInstances);
  yield call(
    dbTabsSagas.getAllSaga,
    connectionId,
  );

  yield fork(browseWorkflow);
  yield put(replace('/tab/browse'));

  try {
    while (true) {
      const newTabs = yield select(store => (
        R.pathOr([], ['tab', `/connection/${connectionId}`, 'ids'], store)
          .filter(id => !activeTabInstances.get(`/tab/${id}`))
          .map(id => store.tab.byId[id])
      ));

      while (newTabs.length) {
        const tab = newTabs.pop();
        if (tab.category) {
          activeTabInstances.set(
            `/tab/${tab.id}`,
            yield fork(categoryWorkflow, tab.id),
          );
        } else if (tab.query) {
          activeTabInstances.set(
            `/tab/${tab.id}`,
            yield fork(queryWorkflow, tab.id),
          );
        }
      }

      yield take(testAction => (
        testAction.type === 'RECEIVE_ENTITIES' &&
        testAction.payload.keyWindow === `/connection/${connectionId}` &&
        testAction.payload.entities.tabs
      ));
    }
  } finally {
    if (yield cancelled()) {
      // TODO: Save to store before cancel and trigger redirect.
    }
  }
};

export default tabOrchestrator;
